import { createSlice } from '@reduxjs/toolkit';
import type { Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { signupApiEndpoints } from './signup-api';

export interface SignupState {
  /**
   * Email the user registered or signed in with. The access token does not carry
   * it, and the profile endpoint requires MFA, so this is the only source for the
   * email confirmation page. Kept in memory only — not persisted.
   */
  email: string;
  fullName: string;
  company: string;
  referredBy: string;
  error: {
    title: string;
    description?: string;
  } | null;
  confirmationResend?: 'resend' | 'no';
  code2FA?: string;
  isLoading?: boolean;
  isRecovery?: boolean;
}

export const signupInitialState: SignupState = {
  email: '',
  fullName: '',
  company: '',
  referredBy: '',
  error: null,
  isLoading: false,
  isRecovery: false,
};

const signupSlice = createSlice({
  name: 'signup',
  initialState: signupInitialState,
  reducers: {
    setEmail(state, action: PayloadAction<string>) {
      state.email = action.payload;
    },
    setReferredBy(state, action: PayloadAction<string>) {
      state.referredBy = action.payload;
    },
    setIsRecovery(state, action: PayloadAction<boolean>) {
      state.isRecovery = action.payload;
    },
    resetSignupState(_state) {
      return signupInitialState;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(signupApiEndpoints.register.matchFulfilled, (state, action) => {
      state.email = action.meta.arg.originalArgs.registerBody.email;
    });
    builder.addMatcher(signupApiEndpoints.login.matchFulfilled, (state, action) => {
      state.email = action.meta.arg.originalArgs.loginBody.email;
    });
    builder.addMatcher(signupApiEndpoints.logout.matchFulfilled, (state) => {
      state.email = '';
    });
    builder.addMatcher(signupApiEndpoints.verifyMfa.matchFulfilled, (state) => {
      state.isRecovery = false;
    });
    builder.addMatcher(signupApiEndpoints.recoverMfa.matchFulfilled, (state) => {
      state.isRecovery = true;
    });
  },
});

export const signupReducer = signupSlice.reducer;
export const signupActions = signupSlice.actions;

/** Any action created by {@link signupActions}. */
export type SignupAction = ReturnType<(typeof signupActions)[keyof typeof signupActions]>;

/**
 * A dispatch narrowed to this slice's actions, for consumers that only write
 * the signup state (`useDispatch<SignupDispatch>()`). The app's root dispatch
 * satisfies it.
 */
export type SignupDispatch = Dispatch<SignupAction>;
